import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, PageHeader, Pill, EmptyState, Spinner } from "@/components/common";
import { toast } from "sonner";
import { Palette, Sparkles, Package } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = ["t-shirt", "mug", "poster", "wall-art", "hoodie"];

export default function Designs() {
  const [prompt, setPrompt] = useState("");
  const [type, setType] = useState("t-shirt");
  const [niche, setNiche] = useState("");
  const [designs, setDesigns] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assembleFor, setAssembleFor] = useState(null);
  const [chosenListing, setChosenListing] = useState("");
  const [assembleType, setAssembleType] = useState("t-shirt");
  const [assembling, setAssembling] = useState(false);

  const load = () => {
    api.get("/designs").then(({ data }) => setDesigns(data)).catch(() => {});
    api.get("/listings").then(({ data }) => setListings(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the design concept first."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/designs/generate", { prompt, product_type: type, niche });
      setDesigns((p) => [data, ...p]);
      toast.success("Design generated.");
      setPrompt("");
    } catch (e) {
      toast.error(apiError(e, "Design generation failed"));
    } finally {
      setLoading(false);
    }
  };

  const openAssemble = (d) => {
    setAssembleFor(d);
    setAssembleType(d.product_type || "t-shirt");
    setChosenListing("");
  };

  const assemble = async () => {
    if (!chosenListing) { toast.error("Pick a listing to attach."); return; }
    setAssembling(true);
    try {
      await api.post("/products/assemble", { design_id: assembleFor.id, listing_id: chosenListing, product_type: assembleType });
      toast.success("Product assembled and sent to the Review Queue for publish approval.");
      window.dispatchEvent(new Event("queue-changed"));
      setAssembleFor(null);
    } catch (e) {
      toast.error(apiError(e, "Assemble failed"));
    } finally {
      setAssembling(false);
    }
  };

  return (
    <>
      <PageHeader title="Design Studio" subtitle="Generate print-ready POD artwork with Gemini Nano Banana. Auto-draft mode — publishing is gated in the Review Queue." testid="designs-header">
        <Pill tone="emerald">Auto-Draft</Pill>
      </PageHeader>

      <Card className="p-5" data-testid="design-input-card">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <input data-testid="design-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Design concept, e.g. 'retro sunset with a cat silhouette, bold line art'" className="lg:col-span-6 px-3 py-2.5 rounded-lg bg-[#12161F] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" />
          <input data-testid="design-niche-input" value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="Niche (optional)" className="lg:col-span-3 px-3 py-2.5 rounded-lg bg-[#12161F] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" />
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="design-type-select" className="lg:col-span-1 bg-[#12161F] border-slate-800 text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#12161F] border-slate-800 text-slate-100">
              {TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize focus:bg-slate-800">{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <button data-testid="generate-design-button" onClick={generate} disabled={loading} className="lg:col-span-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-60 flex items-center gap-2 justify-center">
            {loading ? <Spinner label="Rendering…" /> : <><Sparkles className="w-4 h-4" /> Generate</>}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Image generation can take ~10–20s.</p>
      </Card>

      {designs.length === 0 && !loading ? (
        <Card className="p-6"><EmptyState icon={Palette} title="No designs yet" hint="Describe a concept and generate your first print-ready design." /></Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {designs.map((d) => (
            <Card key={d.id} className="overflow-hidden group" data-testid={`design-card-${d.id}`}>
              <div className="aspect-square bg-[#0C0F14] flex items-center justify-center overflow-hidden">
                <img src={d.image} alt={d.prompt} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <Pill tone="slate">{d.product_type}</Pill>
                  {d.niche && <span className="text-[11px] text-slate-500 truncate max-w-[90px]">{d.niche}</span>}
                </div>
                <p className="text-sm text-slate-300 mt-2 line-clamp-2">{d.prompt}</p>
                <button data-testid={`assemble-design-${d.id}`} onClick={() => openAssemble(d)} className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800/70 text-slate-200 text-sm hover:bg-emerald-500/15 hover:text-emerald-300 border border-slate-700/60 hover:border-emerald-500/30 transition-colors">
                  <Package className="w-4 h-4" /> Assemble Product
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!assembleFor} onOpenChange={(o) => !o && setAssembleFor(null)}>
        <DialogContent className="bg-[#12161F] border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-head">Assemble product</DialogTitle>
            <DialogDescription className="text-slate-400">Attach a listing and set the product type to queue this for publish approval.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-3 items-center">
              {assembleFor && <img src={assembleFor.image} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-800" />}
              <p className="text-sm text-slate-400">Attach a listing and set the product type. This creates a product and sends it to the Review Queue for publish approval.</p>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Listing</label>
              <Select value={chosenListing} onValueChange={setChosenListing}>
                <SelectTrigger data-testid="assemble-listing-select" className="mt-1 bg-[#0C0F14] border-slate-800 text-slate-100">
                  <SelectValue placeholder={listings.length ? "Select a listing" : "No listings — create one first"} />
                </SelectTrigger>
                <SelectContent className="bg-[#12161F] border-slate-800 text-slate-100">
                  {listings.map((l) => <SelectItem key={l.id} value={l.id} className="focus:bg-slate-800">{l.title?.slice(0, 50) || l.topic}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Product type</label>
              <Select value={assembleType} onValueChange={setAssembleType}>
                <SelectTrigger data-testid="assemble-type-select" className="mt-1 bg-[#0C0F14] border-slate-800 text-slate-100"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12161F] border-slate-800 text-slate-100">
                  {TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize focus:bg-slate-800">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="confirm-assemble-button" onClick={assemble} disabled={assembling} className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 disabled:opacity-60">
              {assembling ? "Assembling…" : "Assemble & Queue"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
